import { escapeHtml } from "./acrotohtml/escapeHtml";

/**
 * Document Schema Field Type
 */
export type DocSchemaField = {
  id: string;
  type: "section" | "field" | "text" | "date" | "checkbox" | "checkbox-group" | "radio" | "select";
  label?: string;
  section?: string;
  value?: string | boolean;
  placeholder?: string;
  options?: DocSchemaOption[];
  multiline?: boolean;
  readonly?: boolean;
};

export type DocSchemaOption = {
  label: string;
  value: string;
  checked?: boolean;
};

/**
 * Render a single checkbox field
 */
function renderCheckboxField(field: DocSchemaField): string {
  const id = escapeHtml(field.id);
  const label = field.label ? escapeHtml(field.label) : "";
  const isChecked = field.value ? "checked" : "";

  return `
    <label class="flex items-center gap-2">
      <input 
        type="checkbox" 
        data-id="${id}" 
        class="form-checkbox" 
        ${isChecked}
      />
      <span class="text-sm">${label}</span>
    </label>
  `;
}

/**
 * Render a checkbox group with multiple options
 */
function renderCheckboxGroup(field: DocSchemaField): string {
  const id = escapeHtml(field.id);
  const label = field.label ? escapeHtml(field.label) : "";
  const options = field.options || [];

  const optionsHtml = options
    .map((opt) => {
      const optId = escapeHtml(`${field.id}__${opt.value}`);
      const optLabel = escapeHtml(opt.label);
      const isChecked = opt.checked ? "checked" : "";

      return `
        <label class="flex items-center gap-2 py-1">
          <input 
            type="checkbox" 
            data-id="${optId}" 
            class="form-checkbox" 
            ${isChecked}
          />
          <span class="text-sm">${optLabel}</span>
        </label>
      `;
    })
    .join("");

  return `
    <div class="checkbox-group flex flex-col gap-2">
      ${label ? `<div class="text-sm font-medium text-gray-700">${label}</div>` : ""}
      <div class="divide-y divide-gray-100">
        ${optionsHtml}
      </div>
    </div>
  `;
}

/**
 * Render a radio button group
 */
function renderRadioGroup(field: DocSchemaField): string {
  const id = escapeHtml(field.id);
  const label = field.label ? escapeHtml(field.label) : "";
  const options = field.options || [];

  const optionsHtml = options
    .map((opt) => {
      const optId = escapeHtml(`${field.id}__${opt.value}`);
      const optLabel = escapeHtml(opt.label);
      const isChecked = opt.checked ? "checked" : "";

      return `
        <label class="flex items-center gap-2 py-1">
          <input 
            type="radio" 
            name="${id}" 
            value="${escapeHtml(opt.value)}" 
            data-id="${optId}" 
            class="form-radio" 
            ${isChecked}
          />
          <span class="text-sm">${optLabel}</span>
        </label>
      `;
    })
    .join("");

  return `
    <div class="radio-group flex flex-col gap-2">
      ${label ? `<div class="text-sm font-medium text-gray-700">${label}</div>` : ""}
      <div class="divide-y divide-gray-100">
        ${optionsHtml}
      </div>
    </div>
  `;
}

/**
 * Render a select dropdown
 */
function renderSelectField(field: DocSchemaField): string {
  const id = escapeHtml(field.id);
  const label = field.label ? escapeHtml(field.label) : "";
  const options = field.options || [];
  const placeholder = field.placeholder ? escapeHtml(field.placeholder) : "Select an option";

  const optionsHtml = options
    .map((opt) => {
      const optValue = escapeHtml(opt.value);
      const optLabel = escapeHtml(opt.label);
      const isSelected = opt.checked ? "selected" : "";

      return `<option value="${optValue}" ${isSelected}>${optLabel}</option>`;
    })
    .join("");

  return `
    <div class="select-wrapper flex flex-col gap-2">
      ${label ? `<label class="text-sm text-gray-700">${label}</label>` : ""}
      <select data-id="${id}" class="form-select w-full">
        <option value="">${placeholder}</option>
        ${optionsHtml}
      </select>
    </div>
  `;
}

/**
 * Render a text or date input field
 */
function renderInputField(field: DocSchemaField): string {
  const id = escapeHtml(field.id);
  const label = field.label ? escapeHtml(field.label) : "";
  const placeholder = field.placeholder ? escapeHtml(field.placeholder) : "";
  const value = field.value ? escapeHtml(String(field.value)) : "";

  // Determine input type
  const isDateField = field.type === "date" || (field.label && field.label.toLowerCase().includes("date"));
  const isMultiline = field.multiline;
  const inputType = isDateField ? "date" : "text";
  const readonly = field.readonly ? "readonly" : "";

  if (isMultiline) {
    return `
      <div class="textarea-wrapper flex flex-col gap-2">
        ${label ? `<label class="text-sm text-gray-700">${label}</label>` : ""}
        <textarea 
          data-id="${id}" 
          class="form-textarea w-full" 
          placeholder="${placeholder}"
          ${readonly}
        >${value}</textarea>
      </div>
    `;
  }

  return `
    <div class="input-wrapper flex flex-col gap-2">
      ${label ? `<label class="text-sm text-gray-700">${label}</label>` : ""}
      <input 
        type="${inputType}" 
        data-id="${id}" 
        class="form-input w-full" 
        placeholder="${placeholder}"
        value="${value}"
        ${readonly}
      />
    </div>
  `;
}

/**
 * Render a single field based on its type
 */
function renderField(field: DocSchemaField): string {
  switch (field.type) {
    case "checkbox":
      return renderCheckboxField(field);
    case "checkbox-group":
      return renderCheckboxGroup(field);
    case "radio":
      return renderRadioGroup(field);
    case "select":
      return renderSelectField(field);
    case "text":
    case "date":
    case "field":
      return renderInputField(field);
    default:
      return "";
  }
}

/**
 * Render a section header
 */
function renderSectionHeader(section: DocSchemaField): string {
  const id = escapeHtml(section.id);
  const label = section.label ? escapeHtml(section.label) : "";

  return `
    <div class="section-header mb-4" id="${id}">
      <h2 class="text-lg font-semibold text-gray-900 border-b-2 border-blue-500 pb-2">
        ${label}
      </h2>
    </div>
  `;
}

/**
 * Main function to convert JSON schema to HTML
 * Groups fields by section and renders complete HTML document
 */
export function docToHtml(docSchema: unknown[]): string {
  if (!Array.isArray(docSchema)) {
    return "<div class='error'>Invalid document schema</div>";
  }

  // Separate sections and fields
  const sections = docSchema.filter((item) => item.type === "section") as DocSchemaField[];
  const fields = docSchema.filter((item) => item.type !== "section") as DocSchemaField[];

  // Create a map of fields by section, maintaining order
  const fieldsBySection: Record<string, DocSchemaField[]> = {};
  const sectionOrder: string[] = [];

  fields.forEach((field) => {
    const sectionId = field.section || "general";
    if (!fieldsBySection[sectionId]) {
      fieldsBySection[sectionId] = [];
      sectionOrder.push(sectionId);
    }
    fieldsBySection[sectionId].push(field);
  });

  // Create a map of section details by ID
  const sectionMap: Record<string, DocSchemaField> = {};
  sections.forEach((section) => {
    sectionMap[section.id] = section;
  });

  // Build sections HTML with fields, preserving order
  let sectionsHtml = "";
  sectionOrder.forEach((sectionId) => {
    const sectionFields = fieldsBySection[sectionId] || [];
    const sectionData = sectionMap[sectionId];

    // Render section header if it exists
    let headerHtml = "";
    if (sectionData) {
      headerHtml = renderSectionHeader(sectionData);
    }

    // Render fields in this section
    const fieldsHtml = sectionFields
      .map((field) => `<div class="field mb-4">${renderField(field)}</div>`)
      .join("");

    sectionsHtml += `
      <section class="section mb-6" id="section-${escapeHtml(sectionId)}">
        ${headerHtml}
        <div class="section-fields space-y-4">
          ${fieldsHtml}
        </div>
      </section>
    `;
  });

  // Build complete HTML document
  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Document Form</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          background-color: #f5f5f5;
          padding: 20px;
        }

        .container {
          max-width: 900px;
          margin: 0 auto;
          background-color: white;
          padding: 40px;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .form-title {
          font-size: 28px;
          font-weight: bold;
          margin-bottom: 30px;
          color: #1f2937;
          border-bottom: 3px solid #3b82f6;
          padding-bottom: 15px;
        }

        .section {
          margin-bottom: 30px;
          padding: 20px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background-color: #fafafa;
        }

        .section-header {
          margin-bottom: 20px;
        }

        .section-header h2 {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          border-bottom: 2px solid #3b82f6;
          padding-bottom: 10px;
        }

        .section-fields {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .checkbox-group,
        .radio-group,
        .input-wrapper,
        .textarea-wrapper,
        .select-wrapper {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        label {
          font-size: 14px;
          font-weight: 500;
          color: #374151;
        }

        input[type="text"],
        input[type="date"],
        textarea,
        select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 14px;
          font-family: inherit;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        input[type="text"]:focus,
        input[type="date"]:focus,
        textarea:focus,
        select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        input[type="checkbox"],
        input[type="radio"] {
          cursor: pointer;
          width: 16px;
          height: 16px;
        }

        textarea {
          resize: vertical;
          min-height: 100px;
        }

        .divide-y > *:not(:last-child) {
          border-bottom: 1px solid #f3f4f6;
        }

        .error {
          background-color: #fee2e2;
          border: 1px solid #fecaca;
          color: #991b1b;
          padding: 12px;
          border-radius: 4px;
          font-size: 14px;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="form-title">Document Form</div>
        ${sectionsHtml}
      </div>
    </body>
    </html>
  `;

  return html;
}

/**
 * Convert JSON schema to HTML content (without full document wrapper)
 * Useful for embedding in existing pages
 */
export function docToHtmlContent(docSchema: unknown[]): string {
  if (!Array.isArray(docSchema)) {
    return "<div class='error'>Invalid document schema</div>";
  }

  // Separate sections and fields
  const sections = docSchema.filter((item) => item.type === "section") as DocSchemaField[];
  const fields = docSchema.filter((item) => item.type !== "section") as DocSchemaField[];

  // Create a map of fields by section, maintaining order
  const fieldsBySection: Record<string, DocSchemaField[]> = {};
  const sectionOrder: string[] = [];

  fields.forEach((field) => {
    const sectionId = field.section || "general";
    if (!fieldsBySection[sectionId]) {
      fieldsBySection[sectionId] = [];
      sectionOrder.push(sectionId);
    }
    fieldsBySection[sectionId].push(field);
  });

  // Create a map of section details by ID
  const sectionMap: Record<string, DocSchemaField> = {};
  sections.forEach((section) => {
    sectionMap[section.id] = section;
  });

  // Build sections HTML with fields, preserving order
  let sectionsHtml = "";
  sectionOrder.forEach((sectionId) => {
    const sectionFields = fieldsBySection[sectionId] || [];
    const sectionData = sectionMap[sectionId];

    // Render section header if it exists
    let headerHtml = "";
    if (sectionData) {
      headerHtml = renderSectionHeader(sectionData);
    }

    // Render fields in this section
    const fieldsHtml = sectionFields
      .map((field) => `<div class="field mb-4">${renderField(field)}</div>`)
      .join("");

    sectionsHtml += `
      <section class="section mb-6" id="section-${escapeHtml(sectionId)}">
        ${headerHtml}
        <div class="section-fields space-y-4">
          ${fieldsHtml}
        </div>
      </section>
    `;
  });

  return `
    <div class="form-content">
      ${sectionsHtml}
    </div>
  `;
}
